import React, { useState, useMemo } from 'react';
import { Table, Button, Space, Input, Select, Alert, message, Card, ConfigProvider } from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useAppDispatch } from '@/redux/hooks';
import { closeDialog, setDialog } from '@/redux/reducers/dialog';
import { Confirmation } from '@/components/Confirmation';
import { SpaceAssignmentTable } from './SpaceAssignmentTable';
import { InviteUserModal } from './InviteUserModal';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_USERS,
  UPDATE_USER,
  DELETE_USER,
  GetUsersQueryResponse,
  UpdateUserMutationVariables,
  DeleteUserMutationVariables,
} from './Users';
import { AUTH_API_URL } from './SpaceAssignments';

const { Option } = Select;

interface UserTableRecord {
  key: string;
  id: string;
  name: string;
  email: string;
  role: string;
  lastActiveAt?: string;
}

export const UserManagement = () => {
  const dispatch = useAppDispatch();
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<{ name: string; role: string }>({
    name: '',
    role: '',
  });
  const [searchText, setSearchText] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);

  // Fetch users using GraphQL from auth API

  const {
    data: usersData,
    loading: isLoading,
    error,
    refetch,
  } = useQuery<GetUsersQueryResponse>(GET_USERS, {
    variables: { limit: 100, offset: 0 },
    context: {
      uri: AUTH_API_URL,
    },
  });

  const [updateUser, { loading: isUpdating }] = useMutation<any, UpdateUserMutationVariables>(
    UPDATE_USER,
    {
      context: {
        uri: AUTH_API_URL,
      },
    },
  );
  const [deleteUser, { loading: isDeleting }] = useMutation<any, DeleteUserMutationVariables>(
    DELETE_USER,
    {
      context: {
        uri: AUTH_API_URL,
      },
    },
  );

  // Transform GraphQL data to table format
  const tableData: UserTableRecord[] = useMemo(() => {
    const users = usersData?.users?.users || [];
    return users.map((user) => ({
      key: user.id,
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.roles?.includes('admin') ? 'Admin' : 'Member',
      lastActiveAt: undefined, // Will be populated when API supports it
    }));
  }, [usersData]);

  // Filter table data based on search text
  const filteredTableData = useMemo(() => {
    if (!searchText.trim()) return tableData;
    const lowerSearch = searchText.toLowerCase();
    return tableData.filter(
      (user) =>
        user.name?.toLowerCase().includes(lowerSearch) ||
        user.email?.toLowerCase().includes(lowerSearch) ||
        user.role?.toLowerCase().includes(lowerSearch),
    );
  }, [tableData, searchText]);

  // Format relative time for last updated
  const formatLastUpdated = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Handle refresh
  const handleRefresh = () => {
    refetch();
    setLastUpdated(new Date());
  };

  // Handle export (mock implementation)
  const handleExport = () => {
    message.info('Export functionality coming soon');
  };

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleInviteSuccess = () => {
    setIsInviteModalOpen(false);
    handleRefresh();
  };

  const handleEditUser = (userId: string, name: string, role: string) => {
    setEditingUserId(userId);
    setEditingData({ name, role });
  };

  const handleSaveEdit = async (userId: string) => {
    try {
      if (!editingData.name.trim()) {
        message.error('Name cannot be empty');
        return;
      }
      const roles = editingData.role === 'Admin' ? ['admin'] : ['member'];
      await updateUser({
        variables: {
          id: userId,
          input: {
            name: editingData.name,
            roles: roles,
          },
        },
      });
      message.success('User updated successfully');
      setEditingUserId(null);
      setEditingData({ name: '', role: '' });
      refetch();
    } catch (err) {
      console.error('Failed to update user:', err);
      message.error('Failed to update user');
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditingData({ name: '', role: '' });
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteUser({
        variables: { id: userId },
      });
      message.success('User deleted successfully');
      dispatch(closeDialog());
      refetch();
    } catch (err) {
      console.error('Failed to delete user:', err);
      message.error('Failed to delete user');
      dispatch(closeDialog());
    }
  };

  const handleConfirmDelete = (userId: string) => {
    dispatch(
      setDialog({
        options: { className: 'rounded-[15px] w-[400px]' },
        content: (
          <Confirmation
            text={'Are you sure you want to delete this user?'}
            onConfirm={() => handleDeleteUser(userId)}
            onClose={() => dispatch(closeDialog())}
          />
        ),
        fixedWidth: 400,
      }),
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.name || '').localeCompare(b.name || '');
      },
      render: (text: string, record: UserTableRecord) => {
        if (editingUserId === record.key) {
          return (
            <Input
              value={editingData.name}
              onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
              placeholder="Enter name"
            />
          );
        }
        return text;
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.email || '').localeCompare(b.email || '');
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.role || '').localeCompare(b.role || '');
      },
      render: (text: string, record: UserTableRecord) => {
        if (editingUserId === record.key) {
          return (
            <Select
              value={editingData.role}
              onChange={(value) => setEditingData({ ...editingData, role: value })}
              placeholder="Select a role"
              style={{ width: '100%' }}
            >
              <Option value="Member">Member</Option>
              <Option value="Admin">Admin</Option>
            </Select>
          );
        }
        return text;
      },
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a.lastActiveAt && !b.lastActiveAt) return 0;
        if (!a.lastActiveAt) return 1;
        if (!b.lastActiveAt) return -1;
        return new Date(a.lastActiveAt).getTime() - new Date(b.lastActiveAt).getTime();
      },
      render: (text: string | undefined) => {
        if (!text) return '-';
        const date = new Date(text);
        return date.toLocaleDateString();
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: UserTableRecord) => {
        if (editingUserId === record.key) {
          return (
            <Space size="middle">
              <Button type="link" onClick={() => handleSaveEdit(record.key)} loading={isUpdating}>
                Save
              </Button>
              <Button type="link" onClick={handleCancelEdit}>
                Cancel
              </Button>
            </Space>
          );
        }
        return (
          <Space size="middle">
            <Button
              type="link"
              onClick={() => handleEditUser(record.key, record.name, record.role)}
            >
              Edit
            </Button>
            <Button
              type="link"
              danger
              onClick={() => handleConfirmDelete(record.key)}
              loading={isDeleting}
            >
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#24252C',
        },
      }}
    >
      <div className="bg-surface-sunken w-full min-h-screen p-6">
        {/* Team Members Section */}
        <Card>
          <div className="font-semibold text-base mb-4">
            Team Members ({filteredTableData.length})
          </div>

          {/* Toolbar */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <Input
                placeholder="Search users..."
                prefix={<SearchOutlined className="text-content-tertiary" />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 240 }}
                allowClear
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-content-secondary text-sm">
                Last updated: {formatLastUpdated(lastUpdated)}
              </span>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} title="Refresh" />
              <Button icon={<DownloadOutlined />} onClick={handleExport} title="Export" />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleOpenInviteModal}
                className="bg-content-primary text-content-inverted hover:bg-content-primary/80"
              >
                Invite New Member
              </Button>
            </div>
          </div>

          {error && (
            <Alert
              message="Error loading users"
              description="Failed to fetch users from the server. Please try again."
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              action={
                <Button size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            />
          )}
          <Table
            columns={columns}
            dataSource={filteredTableData}
            loading={isLoading}
            expandable={{
              expandedRowRender: (record: UserTableRecord) => (
                <SpaceAssignmentTable userId={record.id} onUpdate={handleRefresh} />
              ),
              rowExpandable: () => true,
            }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
            }}
          />
        </Card>

        {/* Invite User Modal */}
        <InviteUserModal
          open={isInviteModalOpen}
          onCancel={() => setIsInviteModalOpen(false)}
          onSuccess={handleInviteSuccess}
        />
      </div>
    </ConfigProvider>
  );
};

export default UserManagement;
